import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, ApiError } from '../../api/client'
import type {
  CampaignApplyResult,
  CampaignImportResult,
  CampaignLine,
  CentralCampaign,
  Item,
  Store,
  StoreSize,
} from '../../api/types'
import { useStores } from '../../app/StoreContext'
import { Button, Empty, ErrorBox, Field, Num, Spinner, plural } from '../../ui/primitives'

/** I cluster nell'ordine in cui li pensa chi lavora: dal negozio più piccolo al più grande. */
const SIZES: { code: StoreSize; label: string }[] = [
  { code: 'XS', label: 'Extra small' },
  { code: 'S', label: 'Small' },
  { code: 'M', label: 'Medium' },
  { code: 'L', label: 'Large' },
  { code: 'XL', label: 'Extra large' },
]

type Target = 'all' | 'size' | 'manual'

/**
 * Composizione di una campagna centralizzata: la sede sceglie un articolo, decide a chi mandarlo
 * e in che quantità, e applica.
 *
 * <p>Il modo di scegliere i destinatari è il punto della funzione. Nel sistema precedente la sede
 * distribuiva per <b>cluster dimensionale</b> — «cinquanta pezzi a tutti i Large» — e senza quello
 * bisognerebbe compilare centoventitré righe a mano. Qui i tre modi convivono: tutti, per
 * dimensione, oppure una scelta puntuale.</p>
 *
 * <p>Le righe si accumulano in memoria e si salvano insieme, perché l'API sostituisce l'intero
 * elenco: salvare riga per riga cancellerebbe le altre.</p>
 */
export function CampaignEditor({
  campaign,
  onChanged,
  onClose,
}: {
  campaign: CentralCampaign
  onChanged: () => void
  onClose: () => void
}) {
  const { stores } = useStores()

  const [lines, setLines] = useState<CampaignLine[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const [saving, setSaving] = useState(false)
  const [applying, setApplying] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [applied, setApplied] = useState<CampaignApplyResult>()
  const [problem, setProblem] = useState<string>()

  const [importing, setImporting] = useState(false)
  const [imported, setImported] = useState<CampaignImportResult>()
  const fileInput = useRef<HTMLInputElement>(null)

  // --- scelta dell'articolo -----------------------------------------------------------------
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<Item[]>()
  const [searching, setSearching] = useState(false)
  const [item, setItem] = useState<Item>()
  const timer = useRef<number>(undefined)

  // --- scelta dei destinatari ---------------------------------------------------------------
  const [target, setTarget] = useState<Target>('size')
  const [sizes, setSizes] = useState<StoreSize[]>([])
  const [manual, setManual] = useState<string[]>([])
  const [quantity, setQuantity] = useState('')

  const editable = campaign.status === 'Draft'

  // I magazzini non sono destinatari di un ordine speciale: la merce va nei negozi.
  const shops = useMemo(() => stores.filter((s) => !s.isCedi && s.isActive), [stores])

  const byCluster = useMemo(() => {
    const map = new Map<string, Store[]>()
    for (const s of shops) map.set(s.size ?? '—', [...(map.get(s.size ?? '—') ?? []), s])
    return map
  }, [shops])

  const load = useCallback(() => {
    setLoading(true)
    setError(undefined)
    api
      .get<CampaignLine[]>(`/special-orders/campaigns/${campaign.id}/lines`)
      .then(setLines)
      .catch(setError)
      .finally(() => setLoading(false))
  }, [campaign.id])

  useEffect(load, [load])
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const search = (text: string) => {
    window.clearTimeout(timer.current)
    if (text.trim().length < 2) {
      setResults(undefined)
      return
    }
    timer.current = window.setTimeout(() => {
      setSearching(true)
      api
        .get<Item[]>('/items', {
          search: text.trim(),
          supplierCode: campaign.supplier,
          take: 25,
        })
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false))
    }, 300)
  }

  /** I punti vendita che riceveranno la quantità, secondo il modo scelto. */
  const recipients = useMemo(() => {
    if (target === 'all') return shops.map((s) => s.code)
    if (target === 'size') return shops.filter((s) => s.size && sizes.includes(s.size)).map((s) => s.code)
    return manual
  }, [target, shops, sizes, manual])

  const addLines = () => {
    const qty = Number(quantity)
    setProblem(undefined)

    if (!item) return setProblem('Scegliere prima un articolo.')
    if (!Number.isFinite(qty) || qty < 0) return setProblem('La quantità deve essere zero o più.')
    if (recipients.length === 0) return setProblem('Nessun punto vendita fra i destinatari scelti.')

    // Una riga per punto vendita. Se l'articolo c'era già per quel negozio, la nuova quantità
    // sostituisce la precedente: è l'ultima parola della sede, non una somma involontaria.
    setLines((current) => {
      const rest = (current ?? []).filter(
        (l) => !(l.item === item.code && recipients.includes(l.store)),
      )
      return [...rest, ...recipients.map((store) => ({ store, item: item.code, quantity: qty }))]
    })

    setQuantity('')
  }

  const removeItem = (code: string) =>
    setLines((current) => (current ?? []).filter((l) => l.item !== code))

  const save = async () => {
    setSaving(true)
    setProblem(undefined)
    try {
      // La lettura restituisce `store`/`item`, la scrittura vuole `storeCode`/`itemCode`:
      // due nomi per lo stesso dato, e mandare la forma sbagliata fa fallire la validazione.
      await api.put(
        `/special-orders/campaigns/${campaign.id}/lines`,
        (lines ?? []).map((l) => ({
          storeCode: l.store,
          itemCode: l.item,
          quantity: l.quantity,
        })),
      )
      onChanged()
      load()
    } catch (e: unknown) {
      setProblem(e instanceof ApiError ? (e.detail ?? e.title) : 'Salvataggio non riuscito.')
    } finally {
      setSaving(false)
    }
  }

  // L'import scrive direttamente la bozza sul server (le coppie del file sostituiscono le
  // omologhe, il resto resta): si ricaricano le righe invece di fondere due verità in memoria.
  const importExcel = async (file: File) => {
    setImporting(true)
    setProblem(undefined)
    setImported(undefined)
    try {
      const result = await api.upload<CampaignImportResult>(
        `/special-orders/campaigns/${campaign.id}/lines/import`,
        file,
      )
      setImported(result)
      onChanged()
      load()
    } catch (e: unknown) {
      setProblem(e instanceof ApiError ? (e.detail ?? e.title) : 'Import non riuscito.')
    } finally {
      setImporting(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const apply = async () => {
    const totale = (lines ?? []).length
    if (
      !window.confirm(
        [
          `Applicare la campagna «${campaign.name}»?`,
          '',
          `${plural(totale, 'riga verrà scritta', 'righe verranno scritte')} negli ordini speciali dei punti vendita.`,
          campaign.mode === 'Replace' ?
            'Modalità Sostituisci: le quantità esistenti vengono rimpiazzate, e una quantità a zero annulla la richiesta.'
          : 'Modalità Somma: le quantità si aggiungono a quelle già presenti.',
        ].join('\n'),
      )
    )
      return

    setApplying(true)
    setProblem(undefined)
    try {
      // Il server applica le righe SALVATE, non quelle a video: senza questo salvataggio una
      // campagna composta e applicata al volo si chiudeva vuota — successo apparente, zero
      // richieste nei negozi, e «vedi righe» che non mostrava niente.
      await api.put(
        `/special-orders/campaigns/${campaign.id}/lines`,
        (lines ?? []).map((l) => ({
          storeCode: l.store,
          itemCode: l.item,
          quantity: l.quantity,
        })),
      )
      setApplied(await api.post<CampaignApplyResult>(`/special-orders/campaigns/${campaign.id}/apply`))
      onChanged()
    } catch (e: unknown) {
      setProblem(e instanceof ApiError ? (e.detail ?? e.title) : 'Applicazione non riuscita.')
    } finally {
      setApplying(false)
    }
  }

  /**
   * Annulla la campagna. Una bozza smette di vincolare i suoi articoli (finché è in bozza i
   * negozi non possono richiederli per conto proprio); una campagna applicata ritira anche le
   * richieste ancora in sospeso nei punti vendita.
   */
  const cancelCampaign = async () => {
    if (
      !window.confirm(
        campaign.status === 'Draft' ?
          `Annullare la bozza «${campaign.name}»? Le righe restano consultabili e gli articoli tornano richiedibili dai negozi.`
        : `Annullare la campagna «${campaign.name}»? Le richieste ancora in sospeso nei punti vendita verranno ritirate.`,
      )
    )
      return

    setCancelling(true)
    setProblem(undefined)
    try {
      await api.post(`/special-orders/campaigns/${campaign.id}/cancel`)
      onChanged()
      onClose()
    } catch (e: unknown) {
      setProblem(e instanceof ApiError ? (e.detail ?? e.title) : 'Annullamento non riuscito.')
    } finally {
      setCancelling(false)
    }
  }

  // Le righe si leggono per articolo: un articolo su ottanta negozi è una cosa sola, non ottanta.
  const grouped = useMemo(() => {
    const map = new Map<string, { stores: number; pieces: number; quantities: Set<number> }>()
    for (const l of lines ?? []) {
      const g = map.get(l.item) ?? { stores: 0, pieces: 0, quantities: new Set<number>() }
      g.stores += 1
      g.pieces += l.quantity
      g.quantities.add(l.quantity)
      map.set(l.item, g)
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [lines])

  if (loading) return <Spinner />
  if (error) return <ErrorBox error={error} onRetry={load} />

  return (
    <div className="campaign">
      <div className="campaign-head">
        <h3>
          {campaign.name} <span className="muted">#{campaign.id}</span>
        </h3>
        <div className="card-actions">
          {editable && (
            <>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) void importExcel(f)
                }}
              />
              <Button
                onClick={() => fileInput.current?.click()}
                disabled={saving || applying || importing}
                title="Foglio .xlsx con colonne PDV, Articolo, Quantità (l'intestazione è riconosciuta; senza, valgono le prime tre colonne)"
              >
                {importing ? 'Import in corso…' : 'Importa da Excel'}
              </Button>
              <Button onClick={() => void save()} disabled={saving || applying || importing}>
                {saving ? 'Salvataggio…' : 'Salva righe'}
              </Button>
              <Button
                variant="primary"
                onClick={() => void apply()}
                disabled={applying || saving || (lines ?? []).length === 0}
                title={
                  (lines ?? []).length === 0 ?
                    'La campagna non ha righe da applicare.'
                  : 'Scrive le righe negli ordini speciali dei punti vendita'
                }
              >
                {applying ? 'Applicazione…' : 'Applica campagna'}
              </Button>
            </>
          )}
          {campaign.status !== 'Cancelled' && (
            <Button
              variant="danger"
              onClick={() => void cancelCampaign()}
              disabled={cancelling || saving || applying || importing}
              title={
                campaign.status === 'Draft' ?
                  'Chiude la bozza: gli articoli tornano richiedibili dai negozi'
                : 'Ritira le richieste ancora in sospeso nei punti vendita'
              }
            >
              {cancelling ? 'Annullamento…' : 'Annulla campagna'}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Chiudi
          </Button>
        </div>
      </div>

      {applied && (
        <div className="notice notice-ok">
          <strong>Campagna applicata</strong>
          <p>
            <Num value={applied.created} /> richieste create, <Num value={applied.updated} />{' '}
            aggiornate, <Num value={applied.removed} /> annullate.
          </p>
        </div>
      )}

      {problem && <div className="notice notice-error">{problem}</div>}

      {imported && (
        <div className={`notice ${imported.skipped === 0 ? 'notice-ok' : 'notice-warn'}`}>
          <strong>
            {plural(imported.imported, 'riga importata', 'righe importate')}
            {imported.skipped > 0 && `, ${plural(imported.skipped, 'scartata', 'scartate')}`} · la
            bozza ha ora {plural(imported.totalLines, 'riga', 'righe')}
          </strong>
          {imported.problems.length > 0 && (
            <ul className="import-problems">
              {imported.problems.map((p) => (
                <li key={`${p.rowNumber}-${p.item ?? ''}`}>
                  Riga {p.rowNumber}
                  {p.store && ` · ${p.store}`}
                  {p.item && ` · ${p.item}`}: {p.reason}
                </li>
              ))}
              {imported.problemsTruncated && <li>… e altre oltre le prime 100.</li>}
            </ul>
          )}
        </div>
      )}

      {!editable && (
        <div className="notice notice-info">
          La campagna è {campaign.status === 'Applied' ? 'già stata applicata' : 'annullata'}: le
          righe restano consultabili ma non si modificano.
        </div>
      )}

      {editable && (
        <div className="campaign-compose">
          <Field
            label="Articolo"
            hint={
              campaign.supplier ?
                `Ricerca fra gli articoli del fornitore ${campaign.supplier}.`
              : 'Almeno due caratteri.'
            }
          >
            <input
              value={item ? `${item.code} — ${item.description}` : term}
              placeholder="Descrizione o codice…"
              onChange={(e) => {
                setItem(undefined)
                setTerm(e.target.value)
                search(e.target.value)
              }}
            />
          </Field>

          {searching && <div className="muted">Ricerca in corso…</div>}

          {!item && results && results.length > 0 && (
            <div className="addline-results">
              {results.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  className="addline-item"
                  onClick={() => {
                    setItem(r)
                    setResults(undefined)
                  }}
                >
                  <span className="addline-item-name">{r.description}</span>
                  <small className="muted">
                    {r.code}
                    {r.supplierName ? ` · ${r.supplierName}` : ''}
                  </small>
                </button>
              ))}
            </div>
          )}

          {!item && results && results.length === 0 && !searching && (
            <div className="muted">Nessun articolo corrisponde.</div>
          )}

          <fieldset className="campaign-target">
            <legend>Destinatari</legend>

            <label className="radio">
              <input
                type="radio"
                checked={target === 'all'}
                onChange={() => setTarget('all')}
              />
              Tutti i punti vendita <span className="muted">({shops.length})</span>
            </label>

            <label className="radio">
              <input
                type="radio"
                checked={target === 'size'}
                onChange={() => setTarget('size')}
              />
              Per dimensione
            </label>

            {target === 'size' && (
              <div className="campaign-sizes">
                {SIZES.map((s) => {
                  const count = byCluster.get(s.code)?.length ?? 0
                  return (
                    <label key={s.code} className={`chip${sizes.includes(s.code) ? ' is-on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={sizes.includes(s.code)}
                        disabled={count === 0}
                        onChange={() =>
                          setSizes((current) =>
                            current.includes(s.code) ?
                              current.filter((x) => x !== s.code)
                            : [...current, s.code],
                          )
                        }
                      />
                      {s.label} <span className="muted">{count}</span>
                    </label>
                  )
                })}
                {(byCluster.get('—')?.length ?? 0) > 0 && (
                  <span className="campaign-nodim muted">
                    {byCluster.get('—')?.length} punti vendita senza dimensione dichiarata non
                    rientrano in nessun cluster.
                  </span>
                )}
              </div>
            )}

            <label className="radio">
              <input
                type="radio"
                checked={target === 'manual'}
                onChange={() => setTarget('manual')}
              />
              Scelti a mano
            </label>

            {target === 'manual' && (
              <select
                multiple
                size={8}
                className="campaign-manual"
                value={manual}
                onChange={(e) =>
                  setManual([...e.target.selectedOptions].map((o) => o.value))
                }
              >
                {shops.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.code} — {s.name}
                    {s.size ? ` (${s.size})` : ''}
                  </option>
                ))}
              </select>
            )}
          </fieldset>

          <div className="campaign-apply-row">
            <Field label="Pezzi per punto vendita">
              <input
                type="number"
                min={0}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addLines()}
              />
            </Field>
            <Button onClick={addLines} disabled={!item}>
              Aggiungi ai {plural(recipients.length, 'destinatario', 'destinatari')}
            </Button>
            <span className="muted campaign-hint">
              {campaign.mode === 'Replace' ?
                'Zero pezzi annulla la richiesta nei punti vendita indicati.'
              : 'In modalità Somma la quantità si aggiunge a quella già presente.'}
            </span>
          </div>
        </div>
      )}

      <h4>Righe della campagna</h4>

      {grouped.length === 0 ?
        <Empty>
          Nessuna riga. Scegli un articolo, indica i destinatari e la quantità, poi salva.
        </Empty>
      : <div className="table-wrap">
          <table className="dense">
            <thead>
              <tr>
                <th>Articolo</th>
                <th className="num">Punti vendita</th>
                <th className="num">Pezzi per negozio</th>
                <th className="num">Pezzi totali</th>
                {editable && <th />}
              </tr>
            </thead>
            <tbody>
              {grouped.map(([code, g]) => (
                <tr key={code}>
                  <td className="num">{code}</td>
                  <td className="num">
                    <Num value={g.stores} />
                  </td>
                  <td className="num">
                    {g.quantities.size === 1 ?
                      <Num value={[...g.quantities][0]} />
                    : <span className="muted">variabili</span>}
                  </td>
                  <td className="num">
                    <strong>
                      <Num value={g.pieces} />
                    </strong>
                  </td>
                  {editable && (
                    <td className="num">
                      <button
                        type="button"
                        className="row-remove"
                        title="Togli questo articolo dalla campagna"
                        onClick={() => removeItem(code)}
                      >
                        ×
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      {editable && (lines ?? []).length > 0 && (
        <p className="muted">
          Le righe sono in bozza: diventano richieste nei punti vendita solo con «Applica
          campagna». «Salva righe» le conserva senza toccare i negozi.
        </p>
      )}
    </div>
  )
}
